<script setup>
// SiteUp CRM: popover ancorado no gatilho e teleportado pro <body>.
//
// Por que teleportar: as faixas de controle de Tarefas e as linhas da lista
// vivem dentro de containers com overflow e z-index proprios. Um painel
// absolute la dentro fica preso no contexto de empilhamento e some atras da
// lista (mesma armadilha ja documentada nos menus da sidebar). Teleportando
// pro body e posicionando a partir do retangulo do gatilho, o painel sempre
// aparece inteiro.
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { onClickOutside } from '@vueuse/core';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  // Largura minima do painel, em px.
  minWidth: { type: Number, default: 224 },
  // Alinhamento horizontal em relacao ao gatilho.
  align: { type: String, default: 'start' },
  label: { type: String, default: '' },
});

const emit = defineEmits(['open', 'close']);

const EDGE = 8;
const GAP = 6;

const isOpen = ref(false);
const triggerRef = ref(null);
const panelRef = ref(null);
const anchorStyle = ref({});
const panelMaxHeight = ref(undefined);

const measure = () => {
  const trigger = triggerRef.value;
  if (!trigger?.getBoundingClientRect) return;
  const rect = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const panel = panelRef.value;
  const width = Math.max(
    props.minWidth,
    panel ? Math.max(panel.offsetWidth || 0, panel.scrollWidth || 0) : 0
  );
  const height = panel
    ? Math.max(panel.scrollHeight || 0, panel.offsetHeight || 0)
    : 0;

  const below = vh - rect.bottom - GAP - EDGE;
  const above = rect.top - GAP - EDGE;
  const openUp = height > below && above > below;

  const style = {};
  if (openUp) style.bottom = `${Math.round(vh - rect.top + GAP)}px`;
  else style.top = `${Math.round(rect.bottom + GAP)}px`;

  const start = props.align === 'end' ? rect.right - width : rect.left;
  style.left = `${Math.round(
    Math.max(EDGE, Math.min(start, Math.max(EDGE, vw - width - EDGE)))
  )}px`;
  style.minWidth = `${props.minWidth}px`;

  const room = Math.max(120, openUp ? above : below);
  panelMaxHeight.value = height > room ? `${Math.round(room)}px` : undefined;
  anchorStyle.value = style;
};

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit('close');
};

const open = async () => {
  isOpen.value = true;
  emit('open');
  measure();
  await nextTick();
  measure();
};

const toggle = () => (isOpen.value ? close() : open());

onClickOutside(panelRef, event => {
  if (triggerRef.value?.contains?.(event.target)) return;
  close();
});

const onKeydown = event => {
  if (event.key === 'Escape' && isOpen.value) {
    // Capture + stopImmediatePropagation: o Escape que fecha este popover nao
    // pode fechar tambem o painel lateral que o contem.
    event.stopImmediatePropagation();
    close();
    triggerRef.value?.focus?.();
  }
};

watch(isOpen, value => {
  if (value) window.addEventListener('resize', measure);
  else window.removeEventListener('resize', measure);
});

onMounted(() => document.addEventListener('keydown', onKeydown, true));
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown, true);
  window.removeEventListener('resize', measure);
});

const panelStyle = computed(() => ({
  ...anchorStyle.value,
  maxHeight: panelMaxHeight.value,
}));

defineExpose({ close, open, isOpen });
</script>

<template>
  <div class="inline-flex">
    <span ref="triggerRef" class="inline-flex">
      <slot name="trigger" :toggle="toggle" :is-open="isOpen" />
    </span>
    <TeleportWithDirection>
      <div
        v-if="isOpen"
        ref="panelRef"
        role="dialog"
        :aria-label="label || undefined"
        :style="panelStyle"
        class="fixed z-[9980] overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 p-2 text-n-slate-12 shadow-xl"
      >
        <slot :close="close" />
      </div>
    </TeleportWithDirection>
  </div>
</template>
